import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle, Loader2, Clock, FileText } from 'lucide-react';

/**
 * Classify a sync-log status into (icon, colour, badge variant, human label).
 * Extracted so the JSX below stays flat.
 */
function classifyStatus(status) {
  const isSuccess = status === 'success' || status === 'completed';
  const isInProgress = status === 'in_progress';
  const isStopped = status === 'stopped';

  let StatusIcon = AlertCircle;
  let iconClass = 'w-5 h-5 text-red-500';
  let badgeVariant = 'destructive';
  let badgeLabel = status;

  if (isSuccess) {
    StatusIcon = CheckCircle;
    iconClass = 'w-5 h-5 text-green-500';
    badgeVariant = 'default';
    badgeLabel = 'Success';
  } else if (isInProgress) {
    StatusIcon = Loader2;
    iconClass = 'w-5 h-5 text-blue-500 animate-spin';
    badgeVariant = 'secondary';
    badgeLabel = 'In Progress';
  } else if (isStopped) {
    StatusIcon = Clock;
    iconClass = 'w-5 h-5 text-amber-500';
    badgeVariant = 'outline';
    badgeLabel = 'Stopped';
  }

  return { StatusIcon, iconClass, badgeVariant, badgeLabel };
}

function entryCount(log) {
  return log.entries_synced
    || log.entries_enriched
    || (log.ofac_entries || 0) + (log.opensanctions_entries || 0)
    || 0;
}

/**
 * Recent sync activity list with status-classified rows.
 */
export default function SyncLogsList({ syncLogs, formatDate, formatNumber }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Recent Sync Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {syncLogs.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No sync activity yet</p>
          ) : (
            syncLogs.map((log, index) => {
              const { StatusIcon, iconClass, badgeVariant, badgeLabel } = classifyStatus(log.status);
              return (
                <div
                  key={log.id || log._id || `${log.source}-${log.started_at || index}`}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <StatusIcon className={iconClass} />
                    <div>
                      <p className="font-medium text-slate-900">{log.source}</p>
                      <p className="text-xs text-slate-500">{formatDate(log.timestamp)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900">
                      {formatNumber(entryCount(log))} entries
                    </p>
                    <Badge variant={badgeVariant} className="text-xs">
                      {badgeLabel}
                    </Badge>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
